/**
 * PreviewPlayButton — Reusable hover-overlay play button for cover art.
 *
 * v2.9.82 (2026-06-01)
 * --------------------
 * Used in:
 *   • People's Chart row cover art (RadioChartPage.jsx)
 *   • Stream Monitor SongTable cover art (SongTable.jsx)
 *   • Song Analyzer cover art (SongsAnalyzerPage.jsx)
 *   • Song detail popup (always-visible mode)
 *
 * Behaviour:
 *   • Wraps a cover-art <img> (passed as `src`)
 *   • When `previewUrl` is null/missing → renders just the cover art
 *     with NO button (no clutter, no broken-link state)
 *   • Hover → semi-transparent dark overlay with play icon fades in
 *   • Click → plays preview via the global SoundCharts-style
 *     HTML5 audio singleton; cover art shows a pulsing wave indicator
 *   • Clicking again (or starting another preview) stops the current
 *     audio
 *   • When preview ends or user clicks pause → returns to default state
 *
 * Variants:
 *   • size="sm"   → 36x36 (chart row default)
 *   • size="md"   → 40x40 (stream monitor song table)
 *   • size="lg"   → 96x96 (song detail popup, always-visible button)
 *   • alwaysOn={true} → skip hover gate; show button at rest (popup)
 *
 * Accessibility:
 *   • <button> with aria-label
 *   • Keyboard: Space / Enter trigger play
 *
 * No external dependencies beyond React + lucide-react (already in
 * package.json).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Music } from 'lucide-react';

// ── Global audio singleton ─────────────────────────────────────────────
// Only ONE preview can play at a time across the entire app. The
// singleton lives outside React state so navigating between pages
// doesn't accidentally spawn a new player. Any component that wants to
// know about the current playing track subscribes via _listeners.
const _singleton = {
  audio: null,
  currentKey: null,
  listeners: new Set(),
};

const _notify = () => {
  _singleton.listeners.forEach((cb) => {
    try { cb(_singleton.currentKey); } catch { /* swallow */ }
  });
};

const _stopCurrent = () => {
  if (_singleton.audio) {
    try {
      _singleton.audio.pause();
      _singleton.audio.src = '';
    } catch { /* swallow */ }
    _singleton.audio = null;
  }
  if (_singleton.currentKey !== null) {
    _singleton.currentKey = null;
    _notify();
  }
};

const _playPreview = (previewUrl, key) => {
  if (!previewUrl) return;
  // If clicking the same item that's already playing → toggle to stop
  if (_singleton.currentKey === key) {
    _stopCurrent();
    return;
  }
  _stopCurrent();
  const audio = new Audio(previewUrl);
  audio.preload = 'auto';
  audio.volume = 0.85;
  audio.addEventListener('ended', () => {
    if (_singleton.currentKey === key) _stopCurrent();
  });
  audio.addEventListener('error', () => {
    if (_singleton.currentKey === key) _stopCurrent();
  });
  _singleton.audio = audio;
  _singleton.currentKey = key;
  _notify();
  // Play() returns a promise — swallow rejections (e.g. autoplay policy
  // before any user gesture) silently. The user clicked the button so
  // this should always succeed in practice.
  audio.play().catch(() => _stopCurrent());
};

// Subscribe to the singleton from a React component
const _useSingletonKey = () => {
  const [key, setKey] = useState(_singleton.currentKey);
  useEffect(() => {
    const cb = (k) => setKey(k);
    _singleton.listeners.add(cb);
    return () => _singleton.listeners.delete(cb);
  }, []);
  return key;
};

// ── Size variants ──────────────────────────────────────────────────────
const _sizeMap = {
  sm: { box: 'w-9 h-9', icon: 16, fallback: 'text-xs' },
  md: { box: 'w-10 h-10', icon: 18, fallback: 'text-sm' },
  lg: { box: 'w-24 h-24', icon: 36, fallback: 'text-2xl' },
};

// ── Component ──────────────────────────────────────────────────────────
const PreviewPlayButton = ({
  src,                     // cover art URL (may be null)
  previewUrl,              // 30s MP3 URL (may be null)
  trackKey,                // unique key for this preview (e.g. song id)
  artist = '',             // for aria-label
  title = '',              // for aria-label
  size = 'sm',
  alwaysOn = false,        // always show overlay (popup mode)
  className = '',
  testId = '',
}) => {
  const currentKey = _useSingletonKey();
  const isPlaying = currentKey === trackKey && trackKey != null;
  const hasPreview = Boolean(previewUrl);

  const sizeCfg = _sizeMap[size] || _sizeMap.sm;
  const [hovered, setHovered] = useState(false);

  // Always cleanup if this component unmounts WHILE it's playing
  const playingRef = useRef(isPlaying);
  playingRef.current = isPlaying;
  useEffect(() => () => {
    if (playingRef.current) _stopCurrent();
  }, []);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!hasPreview) return;
    _playPreview(previewUrl, trackKey);
  }, [previewUrl, trackKey, hasPreview]);

  // ── Cover-art-only (no preview available): render thumbnail or
  //    placeholder, no button at all (zero false-positive clutter)
  if (!hasPreview) {
    if (src) {
      return (
        <img
          src={src}
          alt=""
          loading="lazy"
          className={`${sizeCfg.box} rounded object-cover border border-white/10 ${className}`}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          data-testid={testId || 'cover-art-static'}
        />
      );
    }
    return (
      <div
        className={`${sizeCfg.box} rounded bg-white/5 border border-white/10 flex items-center justify-center text-white/30 ${sizeCfg.fallback} ${className}`}
        aria-hidden="true"
      >
        <Music size={sizeCfg.icon * 0.6} />
      </div>
    );
  }

  // ── Cover art WITH preview available: wrap in interactive button ──
  const overlayVisible = alwaysOn || hovered || isPlaying;
  const ariaLabel = isPlaying
    ? `Παύση preview — ${artist || ''} ${title || ''}`.trim()
    : `Άκουσε 30s preview — ${artist || ''} ${title || ''}`.trim();

  return (
    <button
      type="button"
      onClick={handleToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative ${sizeCfg.box} rounded overflow-hidden border border-white/10 group focus:outline-none focus:ring-2 focus:ring-purple-500 ${className}`}
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={testId || 'preview-play-button'}
      data-playing={isPlaying ? 'true' : 'false'}
    >
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-purple-900/40 to-blue-900/40 flex items-center justify-center text-white/40">
          <Music size={sizeCfg.icon * 0.7} />
        </div>
      )}
      <span
        className={`absolute inset-0 flex items-center justify-center bg-black/55 transition-opacity duration-150 ${
          overlayVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        {isPlaying ? (
          <Pause size={sizeCfg.icon} className="text-white drop-shadow-lg" />
        ) : (
          <Play size={sizeCfg.icon} className="text-white drop-shadow-lg fill-white/90" />
        )}
      </span>
      {/* Subtle pulsing ring while playing — pure CSS, no extra deps */}
      {isPlaying && (
        <span
          className="absolute inset-0 rounded ring-2 ring-purple-400 animate-pulse pointer-events-none"
          aria-hidden="true"
        />
      )}
    </button>
  );
};

export default PreviewPlayButton;
